import { makeDiscriminatedUnionPredicate } from '@sgme/fp';
import type { SearchResponse } from 'apiConnector/blotters';
import { secondaryOrderBlotterApiConnector } from 'apiConnector/blotters';
import { isNavigatingInSecondaryOrderBlotter } from 'epic/utils/navigation/navigation';
import { ofType } from 'redux-observable';
import type { Observable, SchedulerLike } from 'rxjs';
import { combineLatest, interval, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  map,
  mergeMap,
  skipUntil,
  startWith,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import type { Action } from 'state/actions';
import { unhandledError } from 'state/actions';
import {
  secondaryMarketOrderBlotterColumnDefinitionReceived,
  secondaryMarketOrderBlotterItemsFetchingFailed,
  secondaryMarketOrderBlotterItemsReceived,
  secondaryMarketOrderBlotterItemsReloaded,
  secondaryMarketOrderBlotterItemsRequested,
  secondaryMarketOrderBlotterSetColumnFilter,
} from 'state/blotters/secondaryMarketOrderBlotter';
import type { EpicWithThunk } from 'state/state.common';

function isNavigatingOut(action: Action) {
  return action.type === 'ROUTE_CHANGED' && action.path !== '/secondary-orders-blotter';
}

export function pollSecondaryMarketBlotterItemsEpic(
  pollingFrequency: number = 10_000,
  scheduler?: SchedulerLike,
): EpicWithThunk {
  return (action$, state$, { selectors: { isInSecondaryMarketOrderBlotterRoute }, isPageVisible }) => {
    const navigationOut$: Observable<Action> = action$.pipe(filter(isNavigatingOut));

    return action$.pipe(
      filter(
        action =>
          action.type === 'SECONDARY_MARKET_ORDER_BLOTTER_ITEMS_RECEIVED' ||
          isNavigatingInSecondaryOrderBlotter(action),
      ),
      withLatestFrom(state$),
      filter(([, state]) => isInSecondaryMarketOrderBlotterRoute(state)),
      switchMap(([, state]) =>
        interval(pollingFrequency, scheduler).pipe(
          filter(() => {
            return isInSecondaryMarketOrderBlotterRoute(state) && isPageVisible();
          }),
          takeUntil(navigationOut$),
        ),
      ),
      map(() => secondaryMarketOrderBlotterItemsReloaded()),
    );
  };
}

const isFetchingAction = makeDiscriminatedUnionPredicate('type', [
  'SECONDARY_MARKET_ORDER_BLOTTER_ITEMS_RELOADED',
  'SECONDARY_MARKET_ORDER_BLOTTER_PREFERENCE_STATE_LOADED',
] satisfies Action['type'][]);

export function fetchItemsEpic(aggregationFrequency: number = 500, scheduler?: SchedulerLike): EpicWithThunk {
  return (action$, _state$, { httpClient, frontLogger }) => {
    return action$.pipe(
      filter(isFetchingAction),
      skipUntil(action$.pipe(filter(({ type }) => type === 'SECONDARY_MARKET_ORDER_BLOTTER_PREFERENCE_STATE_LOADED'))),
      debounceTime(aggregationFrequency, scheduler),
      switchMap(() => {
        return secondaryOrderBlotterApiConnector(httpClient)
          .search()
          .pipe(
            map(res => {
              if (res === null) {
                const EMPTYResult: SearchResponse = {
                  pageInfo: { page: 0, size: 0, totalElements: 0, totalPages: 0 },
                  entries: [],
                };
                return EMPTYResult;
              }
              return res;
            }),
            map(({ entries }) => secondaryMarketOrderBlotterItemsReceived(entries)),
            startWith(secondaryMarketOrderBlotterItemsRequested()),
            catchError(err => {
              frontLogger.error('secondary market order-blotter failed to fetch items: {@FrontError}', err);
              return of(secondaryMarketOrderBlotterItemsFetchingFailed(err.message));
            }),
          );
      }),
    );
  };
}

export function fetchColumnDefinitionEpic(): EpicWithThunk {
  return (actions$, _state$, { httpClient }) =>
    actions$.pipe(
      filter(isNavigatingInSecondaryOrderBlotter),
      take(1),
      mergeMap(_ => {
        return secondaryOrderBlotterApiConnector(httpClient)
          .fetchColumnDefinition()
          .pipe(
            map(secondaryMarketOrderBlotterColumnDefinitionReceived),
            catchError(err =>
              of(
                unhandledError(
                  'Failed to fetch columns definitions for secondary market order blotter',
                  err,
                  'fetchColumnDefinitionEpic',
                ),
              ),
            ),
          );
      }),
    );
}

export function waitUntilBlotterLoadedToSetDefaultFilter(): EpicWithThunk {
  return (actions$, _) => {
    const columnDefinitionLoaded$ = actions$.pipe(ofType('SECONDARY_MARKET_ORDER_BLOTTER_COLUMN_DEFINITION_RECEIVED'));
    const columnPreferenceLoaded$ = actions$.pipe(ofType('SECONDARY_MARKET_ORDER_BLOTTER_PREFERENCE_STATE_LOADED'));
    const filterByOrderId$ = actions$.pipe(ofType('SECONDARY_MARKET_ORDER_BLOTTER_FILTER_BY_ORDER_ID'));

    const result$ = combineLatest([filterByOrderId$, columnDefinitionLoaded$, columnPreferenceLoaded$]).pipe(
      map(([action, _, __]) => {
        return secondaryMarketOrderBlotterSetColumnFilter('orderId', 'multi', [
          {
            filterType: 'text',
            type: 'equals',
            filter: action.orderId,
          },
          null,
        ]);
      }),
    );
    return result$;
  };
}
